import type { MutableRefObject, RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * A hook that allows you have to have state in the component,
 * while also having the ability to access the current value at any time
 *
 * @param initialState - the initial value
 * @return [ref, set state function]
 */
export function useStateWithRef<T = undefined>(): [MutableRefObject<T | undefined>, (state: T | undefined) => void];
export function useStateWithRef<T>(initialState: T): [MutableRefObject<T>, (state: T) => void];
export function useStateWithRef<T>(
  initialState?: T,
): [MutableRefObject<T | undefined>, (state: T | undefined) => void] {
  const [, setState] = useState<T | undefined>(initialState);
  const stateRef = useRef<T | undefined>(initialState);
  return [
    stateRef,
    (state: T | undefined) => {
      stateRef.current = state;
      setState(state);
    },
  ];
}

function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
): void;
function useEventListener<K extends keyof HTMLElementEventMap, T extends HTMLElement = HTMLDivElement>(
  eventName: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  element: RefObject<T>,
): void;

function useEventListener<
  KW extends keyof WindowEventMap,
  KH extends keyof HTMLElementEventMap,
  T extends HTMLElement | void = void,
>(
  eventName: KW | KH,
  handler: (event: WindowEventMap[KW] | HTMLElementEventMap[KH] | Event) => void,
  element?: RefObject<T>,
) {
  // Create a ref that stores handler
  const savedHandler = useRef<typeof handler>(undefined);

  useEffect(() => {
    // Define the listening target
    const targetElement: T | Window = element?.current || window;
    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }

    // Update saved handler if necessary
    if (savedHandler.current !== handler) {
      savedHandler.current = handler;
    }

    // Create event listener that calls handler function stored in ref
    const eventListener: typeof handler = (event) => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!savedHandler?.current) {
        savedHandler.current(event);
      }
    };

    targetElement.addEventListener(eventName, eventListener);

    // Remove event listener on cleanup
    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, handler]);
}
interface Size {
  width: number;
  height: number;
}

function useElementSize<T extends HTMLElement = HTMLDivElement>(): [(node: T | null) => void, Size] {
  // Mutable values like 'ref.current' aren't valid dependencies
  // because mutating them doesn't re-render the component.
  // Instead, we use a state as a ref to be reactive.
  const [ref, setRef] = useState<T | null>(null);
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
  });

  // Prevent too many rendering using useCallback
  const handleSize = useCallback(() => {
    setSize({
      width: ref?.offsetWidth || 0,
      height: ref?.offsetHeight || 0,
    });

    /// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.offsetHeight, ref?.offsetWidth]);

  useEventListener('resize', handleSize);

  useLayoutEffect(() => {
    handleSize();
    /// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.offsetHeight, ref?.offsetWidth]);

  return [setRef, size];
}

export default useElementSize;

export function useCounter(): [number, () => void] {
  const [count, setCount] = useState<number>(0);
  return [count, () => setCount((value) => value + 1)];
}

// React allows the state setter function to be called with a function, which it uses to update the current value, but
// in some cases we want to set the value to a function, and this hook allows us to do so without always wrapping the
// new value in a function.
export function useFunctionState<T>(f: T): [T, (newValue: T) => void] {
  const [value, setValue] = useState<T>(() => f);
  return [value, (newValue) => setValue(() => newValue)];
}
